import { faCheckCircle, faChevronDown, faChevronUp, faExclamationTriangle, faRefresh, faTimeline } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Collapse, OverlayTrigger, Placeholder, Row, Table, Tooltip } from "react-bootstrap";
import { EXPLAIN_CHARTING, EXPLAIN_DDX, SCAN_MULTI } from "../../../amplify/api-gateway/write";
import { getLocalDateStrFromDate, getLocalTimeStrFromDate } from "../../../utils/time";
import { GenerateModuleHeroV2 } from "../../common/GenerateModuleHeroV2";
import { LLMDashboardFeedbackBar } from "../../dashboard/llm/common/LLMDashboardFeedbackBar";
import { LLMDashboardUpdated } from "../../dashboard/llm/common/LLMDashboardUpdated";
import { renderMatchDetails, VerificationState } from "../../dashboard/llm/common/verification";
import { FakeLoadingBar } from "../../dashboard/llm/common/FakeLoadingBar";
import { isSectionUnread } from "../../dashboard/llm/utils";
import { dashboardLLMCache } from "../../dashboard/llm/cache";
import { LLMDashboardMarkdownRenderer } from "../../dashboard/llm/common/LLMDashboardMarkdownRenderer";


export const SectionTrackboardTimeline = ({
    className='',
    isLoadingCharting,
    charting,
    patientVisit,
    feedbacks,
    setFeedbacks,
    statuses,
    setRegenerateTarget,
    setShowContextMenu,
    locations,
    handleShowScanMultiModal,
    tenant,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
}: any) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [timelineEvents, setTimelineEvents] = useState([]);
    const [showEvidenceIndex, setShowEvidenceIndex] = useState<number | null>(null);
    const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && latestFileVersions && latestFileVersions[EXPLAIN_CHARTING]) {
            const version = latestFileVersions[EXPLAIN_CHARTING];

            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
            .then((data: any) => {
                // console.log("[Patient Summary] Found citation_mapping", data.citation_mapping);
                setScanMulti(data);
            })
        }
    }, [latestFileVersions]);

    useEffect(() => {
        const events: any = [];
        if (charting && charting["reasoned_timeline"]) {
            for (let event of charting["reasoned_timeline"]) {
                events.push(event)
            }
        }
        const defaultVerified: VerificationState = {
            "time_verified": true,
            "matches": []
        };
        if (patientVisit) {
            events.push({
                "time_pt": `${getLocalDateStrFromDate(new Date(patientVisit.arrival_time))} ${getLocalTimeStrFromDate(new Date(patientVisit.arrival_time))}`,
                "event_summary": "Arrived",
                "verification": defaultVerified,
            });
            if (patientVisit.roomed_time) {
                events.push({
                    "time_pt": `${getLocalDateStrFromDate(new Date(patientVisit.roomed_time))} ${getLocalTimeStrFromDate(new Date(patientVisit.roomed_time))}`,
                    "event_summary": "Roomed",
                    "verification": defaultVerified,
                });
            }
            if (patientVisit.dispo_time) {
                events.push({
                    "time_pt": `${getLocalDateStrFromDate(new Date(patientVisit.dispo_time))} ${getLocalTimeStrFromDate(new Date(patientVisit.dispo_time))}`,
                    "event_summary": "Dispo",
                    "verification": defaultVerified,
                });
            }
            if (patientVisit.departure_time) {
                events.push({
                    "time_pt": `${getLocalDateStrFromDate(new Date(patientVisit.departure_time))} ${getLocalTimeStrFromDate(new Date(patientVisit.departure_time))}`,
                    "event_summary": "Departure",
                    "verification": defaultVerified,
                });
            }
        }
        if (locations) {
            for (let location of locations) {
                events.push({
                    "time_pt": `${getLocalDateStrFromDate(new Date(location.location_start))} ${getLocalTimeStrFromDate(new Date(location.location_start))}`,
                    "event_summary": `Roomed in ${location.location}`,
                    "verification": defaultVerified,
                });
            }
        }
        events.sort((a: any, b: any) => {
            const nameA = a.time_pt ?? "";
            const nameB = b.time_pt ?? "";
            return nameA.localeCompare(nameB);
        });
        setTimelineEvents(events);
    }, [locations, charting]);

    if (!isLoadingCharting && (!charting || charting["error"])) {
        return (
            <GenerateModuleHeroV2 type={EXPLAIN_CHARTING} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} />
        )
    }

    return (
        <Card className={`fade-in ${className}`}>
            <FakeLoadingBar isActive={isLoadingCharting} maxDuration={40000} />
            <Card.Body>
                <Row>
                    <Col md={4}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faTimeline} fixedWidth /></span><strong>Timeline</strong> {isSectionUnread(EXPLAIN_CHARTING, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={8} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_CHARTING} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />
                        
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_DDX); setShowContextMenu(true)}}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"charting"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && (
                    <>
                        <Row className="mt-2">
                            <Col md={12} className="description-text">
                                {
                                    isLoadingCharting ? (
                                        <Placeholder animation="glow">
                                            <Placeholder xs={4} />
                                        </Placeholder>
                                    ) : charting && charting["reasoned_timeline"] ? (
                                        <Table className="text-small rhythm-table" hover>
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Time</th>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Event</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {
                                                    timelineEvents && timelineEvents.map((item: any, index: number) => {
                                                        // const verification: VerificationState = item.verification;
                                                        return (
                                                            <>
                                                                <tr key={index} className={`description-text mt-2 timeline-section timeline-section-active`}
                                                                    // onClick={() => {
                                                                    //     setShowEvidenceIndex((showEvidenceIndex === null || showEvidenceIndex !== index) ? index : null); 
                                                                    //     // setVerificationState(verification)
                                                                    // }}
                                                                >
                                                                    <td>
                                                                        {item.time_pt}
                                                                    </td>
                                                                    <td>
                                                                        <LLMDashboardMarkdownRenderer content={item.event_summary} scanMulti={scanMulti} />
                                                                        {/* {verification && (
                                                                            <span 
                                                                                style={{ cursor: 'pointer' }}
                                                                                title={verification.time_verified ? 
                                                                                    `Verified (${verification.matches.length} match${verification.matches.length !== 1 ? 'es' : ''})` : 
                                                                                    'Not found in scan data'
                                                                                }
                                                                            >
                                                                                <OverlayTrigger placement="top" overlay={<Tooltip>{verification.time_verified ? <>Time Verified Data</> : <>Unvalidated Data - Use with Caution</>}</Tooltip>}>
                                                                                    <span style={{ cursor: 'pointer' }} className="me-2">
                                                                                        <FontAwesomeIcon 
                                                                                            icon={verification.time_verified ? faCheckCircle : faExclamationTriangle} 
                                                                                            className={verification.time_verified ? "text-success" : "text-warning"}
                                                                                        />
                                                                                    </span>
                                                                                </OverlayTrigger>
                                                                            </span>
                                                                        )} */}
                                                                    </td>
                                                                </tr>
                                                                {/* {verificationState && verificationState.matches.length > 0 && (
                                                                    <Collapse in={showEvidenceIndex === index}>
                                                                        <tr>
                                                                            <td colSpan={2}>
                                                                                <div>
                                                                                    <hr className="my-2" />
                                                                                    <Row>
                                                                                        <Col>
                                                                                            <small className="text-muted">
                                                                                                <strong>Context Data:</strong>
                                                                                            </small>
                                                                                            {renderMatchDetails(verificationState)}
                                                                                        </Col>
                                                                                    </Row>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    </Collapse>
                                                                )} */}
                                                            </>
                                                        );
                                                    })
                                                }
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <>No Data</>
                                    )
                                }
                            </Col>
                        </Row>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
